import React from "react";
import styled from "styled-components";
import { Button } from "antd";
import { Content, MainContainer, MainContent, Title } from "../../../styles/styles";
import { KoTitle1 } from "../../../styles/sharedStyles";
import SideBar from "../../../components/nav/SideBar";
import { useNavigationStore } from "store/navigationStore";
import { useNavigate } from "react-router-dom";
import { KoHeadline } from "../../../styles/sharedStyles";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const PlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 1200px;
`;

const PlanCard = styled.div<{ isCurrentPlan?: boolean }>`
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  border: ${props => props.isCurrentPlan ? '2px solid #4F7EFF' : '1px solid #e5e5e5'};
  position: relative;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  min-height: 480px;
`;

const CurrentPlanBadge = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #4F7EFF;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const PlanName = styled.h3`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #000;
`;

const PlanPrice = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin-bottom: 8px;
`;

const PlanPeriod = styled.div`
  font-size: 14px;
  color: #888;
  margin-bottom: 32px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #666;
  
  &:before {
    content: ">";
    color: #4F7EFF;
    font-weight: 600;
  }
`;

const PlanButton = styled(Button)<{ variant?: 'primary' | 'secondary' }>`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  
  ${props => props.variant === 'primary' ? `
    background: #000;
    border: none;
    color: #fff;
    
    &:hover {
      background: #333 !important;
      color: #fff !important;
    }
  ` : `
    background: transparent;
    border: 1px solid #d0d0d0;
    color: #666;
    
    &:hover {
      background: #f8f8f8 !important;
      border-color: #b0b0b0 !important;
      color: #666 !important;
    }
  `}
`;

const PlanSelectionPage = () => {
  const { isCollapsed } = useNavigationStore();
  const navigate = useNavigate();

  const plans = [
    {
      name: "Free",
      price: "0원",
      period: "월 구독료 (VAT 별도)",
      isCurrentPlan: true,
      features: [
        "1팀 (최대 3인)",
        "월 60분 분석",
        "표준 리포트",
        "7일 데이터 보관"
      ],
      buttonText: "",
      buttonVariant: "secondary" as const
    },
    {
      name: "Team",
      price: "149,000원",
      period: "월 구독료 (VAT 별도)",
      features: [
        "최대 10인",
        "월 1,000분 분석",
        "심화 리포트",
        "90일 데이터 보관",
        "고객 프로파일링"
      ],
      buttonText: "업그레이드",
      buttonVariant: "primary" as const
    },
    {
      name: "Pro",
      price: "349,000원",
      period: "월 구독료 (VAT 별도)",
      features: [
        "최대 30인",
        "월 3,000분 분석",
        "맞춤 리포트",
        "무제한 데이터 보관",
        "API 연동",
        "액션 관리"
      ],
      buttonText: "업그레이드",
      buttonVariant: "primary" as const
    },
    {
      name: "Enterprise",
      price: "별도 문의",
      period: "월 구독료 (VAT 별도)",
      features: [
        "무제한 인원",
        "맞춤 분석량",
        "커스터마이징",
        "온프레미스 지원",
        "전용 SLA",
        "컨설팅 포함"
      ],
      buttonText: "문의하기",
      buttonVariant: "secondary" as const
    }
  ];

  const handlePlanSelect = (planName: string) => {
    if (planName === "Enterprise") {
      // 문의하기 로직 (예: 이메일 또는 문의 페이지로 이동)
      window.location.href = "mailto:contact@company.com";
    } else {
      // 결제 페이지로 이동
      navigate(`/payment-form?plan=${planName.toLowerCase()}`);
    }
  };

  return (
    <MainContainer>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <MainContent>
          <PageContainer>
            <KoHeadline>결제·구독 관리</KoHeadline>
            
            <PlanSection>
              <KoTitle1>플랜 선택</KoTitle1>
              <PlansGrid>
                {plans.map((plan, index) => (
                  <PlanCard key={index} isCurrentPlan={plan.isCurrentPlan}>
                    {plan.isCurrentPlan && <CurrentPlanBadge>현재 플랜</CurrentPlanBadge>}
                    
                    <PlanName>{plan.name}</PlanName>
                    <PlanPrice>{plan.price}</PlanPrice>
                    <PlanPeriod>{plan.period}</PlanPeriod>
                    
                    <FeatureList>
                      {plan.features.map((feature, featureIndex) => (
                        <FeatureItem key={featureIndex}>{feature}</FeatureItem>
                      ))}
                    </FeatureList>
                    
                    {plan.buttonText && (
                      <PlanButton
                        variant={plan.buttonVariant}
                        onClick={() => handlePlanSelect(plan.name)}
                      >
                        {plan.buttonText}
                      </PlanButton>
                    )}
                  </PlanCard>
                ))}
              </PlansGrid>
            </PlanSection>
          </PageContainer>
        </MainContent>
      </Content>
    </MainContainer>
  );
};

export default PlanSelectionPage;